import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { showToast } from '../components/Toast';
import DmrActionItems, { ItemForm } from '../components/DmrActionItems';
import { DMR_HANDOFF_KEY } from '../components/FranchiseeProfile';
import { dmrApi, type DmrItemInput, type DmrRecapDraft } from '../api/dmr';

/**
 * One DMR call, before and after (#216, #252). The checklist itself is ticked
 * off in the Notes drawer of the franchisee's dashboard during the screen
 * share; this page holds the action items, notes, summary, "done" and the
 * recap email. Autosaves. FBCs get it read-only. Franchisees never reach it
 * (the API 404s them).
 */

const NAVY = '#1f2a48';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

function errText(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export default function DmrCallPage() {
  const id = Number(useParams().id);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dmr', 'call', id], queryFn: () => dmrApi.call(id), retry: false });

  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState<{ title?: string } | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!q.data || loaded.current) return;
    loaded.current = true;
    setNotes(q.data.run?.notes ?? '');
    setSummary(q.data.run?.summary ?? '');
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => dmrApi.saveRun(id, { notes, summary }),
    onSuccess: () => {
      setDirty(false);
    },
    onError: (e) => showToast(`Not saved: ${errText(e)}`, { variant: 'error', durationMs: 5000 }),
  });

  // Autosave a moment after the last change, so nothing is lost if the
  // call drops or the tab closes.
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save.mutate(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, notes, summary]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['dmr'] });
  const addItem = useMutation({
    mutationFn: (body: DmrItemInput) => dmrApi.createItem(id, body),
    onSuccess: () => {
      setAdding(null);
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });
  const complete = useMutation({
    mutationFn: async () => {
      if (dirty) await dmrApi.saveRun(id, { notes, summary });
      return dmrApi.completeCall(id);
    },
    onSuccess: () => {
      setDirty(false);
      showToast('Call marked done. A summary note was added to their record.');
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });
  const followup = useMutation({
    mutationFn: () => dmrApi.sendFollowup(id),
    onSuccess: (r) => {
      showToast(r.testMode ? `Test site: recap sent to you only (${r.sent.join(', ')})` : `Recap sent to ${r.sent.length} people`, { durationMs: 4500 });
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  if (q.isLoading) return <Wrap>Loading the call…</Wrap>;
  if (q.error || !q.data) return <Wrap><p style={{ color: MUTED }}>{errText(q.error)}</p></Wrap>;

  const { booking: b, partner, template, actionItems, canEdit } = q.data;
  const mainAf = b.afs.find((a) => !/\.[0-9]+$/.test(a)) ?? b.afs[0];
  const allItems = template?.sections.flatMap((s) => s.items) ?? [];
  const answered = allItems.filter((i) => {
    const r = q.data!.run?.responses[i.id];
    return r && (r.status || (r.value !== undefined && r.value !== null && r.value !== ''));
  }).length;

  return (
    <Wrap>
      <h1 style={{ margin: '10px 0 2px', fontSize: '1.45rem', color: NAVY }}>
        DMR call: {b.partnerName}
      </h1>
      <p style={{ margin: 0, color: MUTED }}>
        {b.startsAt && new Date(b.startsAt).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
        {' · '}with {b.teamMember?.name ?? '—'}
        {b.fbcName && ` · FBC ${b.fbcName}`}
        {b.status !== 'scheduled' && ` · ${b.status === 'completed' ? 'Done' : b.status === 'no_show' ? 'No-show' : 'Cancelled'}`}
      </p>

      <section style={{ ...card, marginTop: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14 }}>
        <div>
          <div style={label}>Open their dashboard</div>
          {b.afs.map((af) => (
            <a key={af} href={`/?af=${encodeURIComponent(af)}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, marginRight: 10 }}>
              {af}
            </a>
          ))}
        </div>
        {partner?.marketingNames.length ? (
          <div>
            <div style={label}>Locations</div>
            {partner.marketingNames.join(' · ')}
          </div>
        ) : null}
        <div>
          <div style={label}>On the invite</div>
          {[b.primaryEmail, ...b.extraAttendees.map((a) => a.name || a.email)].join(', ')}
        </div>
        {b.teamsJoinUrl && (
          <div>
            <div style={label}>Meeting</div>
            <a href={b.teamsJoinUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Join on Teams</a>
          </div>
        )}
      </section>

      <div style={{ display: 'grid', gap: 14, marginTop: 18, maxWidth: 760 }}>
          <section style={{ ...card, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h3 style={{ margin: '0 0 2px', fontSize: 16, color: NAVY }}>Checklist</h3>
              <span style={{ fontSize: 14, color: MUTED }}>
                {answered} of {allItems.length} covered. The checklist lives in the Notes pane of their dashboard, so
                you can tick it off while you share your screen.
              </span>
            </div>
            {canEdit && (
              <Link
                to={`/?af=${encodeURIComponent(mainAf)}`}
                onClick={() => {
                  try {
                    sessionStorage.setItem(DMR_HANDOFF_KEY, mainAf);
                  } catch {
                    /* storage blocked: they can open the Notes drawer by hand */
                  }
                }}
                style={{ ...primaryBtn, textDecoration: 'none' }}
              >
                Open their dashboard
              </Link>
            )}
          </section>
          <section style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: NAVY }}>Action items</h3>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: MUTED }}>Franchisees see the ones marked visible, with their status.</p>
            <DmrActionItems items={actionItems} mode="edit" canEdit={canEdit} />
            {canEdit &&
              (adding ? (
                <div style={{ marginTop: 10 }}>
                  <ItemForm
                    initial={{ title: adding.title ?? '', templateItemId: null }}
                    submitLabel="Add"
                    onSubmit={(body) => addItem.mutate(body)}
                    onCancel={() => setAdding(null)}
                  />
                </div>
              ) : (
                <button style={{ ...linkBtn, marginTop: 8 }} onClick={() => setAdding({})}>+ Add action item</button>
              ))}
          </section>

          {canEdit && q.data.recapEnabled && b.teamsJoinUrl && (
            <RecapCard
              bookingId={id}
              onUseSummary={(text) => { setSummary(text); setDirty(true); }}
              onAddItems={async (items) => {
                for (const it of items) await dmrApi.createItem(id, it);
                refresh();
              }}
            />
          )}

          <section style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: NAVY }}>Notes</h3>
            <textarea
              value={notes}
              disabled={!canEdit}
              onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
              rows={6}
              placeholder="Working notes during the call (marketing and FBCs only)"
              style={textarea}
            />
            <h3 style={{ margin: '12px 0 6px', fontSize: 16, color: NAVY }}>Summary</h3>
            <textarea
              value={summary}
              disabled={!canEdit}
              onChange={(e) => { setSummary(e.target.value); setDirty(true); }}
              rows={4}
              placeholder="A few sentences for their record and the recap email"
              style={textarea}
            />
            <p style={{ margin: '4px 0 0', fontSize: 12, color: MUTED }}>
              The summary goes into the recap email and their Notes. Working notes stay internal.
            </p>
          </section>

          {canEdit && (
            <section style={card}>
              {b.status === 'scheduled' ? (
                <button
                  style={primaryBtn}
                  disabled={complete.isPending}
                  onClick={() => window.confirm('Mark this call done? A summary note is added to each of their AFs (and Zoho).') && complete.mutate()}
                >
                  {complete.isPending ? 'Saving…' : 'Mark call done'}
                </button>
              ) : (
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                  {b.status === 'completed' ? 'Call done.' : b.status === 'no_show' ? 'Marked no-show.' : 'Cancelled.'}
                </p>
              )}
              {b.status === 'completed' && (
                <div style={{ marginTop: 10 }}>
                  <button
                    style={primaryBtn}
                    disabled={followup.isPending}
                    onClick={() => window.confirm(`Email the recap (summary + visible action items) to ${b.partnerName} and their invited staff?`) && followup.mutate()}
                  >
                    {followup.isPending ? 'Sending…' : b.followupSentAt ? 'Send the recap again' : 'Send recap email'}
                  </button>
                  {b.followupSentAt && (
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: MUTED }}>
                      Last sent {new Date(b.followupSentAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}
            </section>
          )}
      </div>
    </Wrap>
  );
}

function RecapCard({
  bookingId,
  onUseSummary,
  onAddItems,
}: {
  bookingId: number;
  onUseSummary: (text: string) => void;
  onAddItems: (items: DmrItemInput[]) => Promise<void>;
}) {
  const [draft, setDraft] = useState<DmrRecapDraft | null>(null);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<boolean[]>([]);
  const make = useMutation({
    mutationFn: () => dmrApi.draftRecap(bookingId),
    onSuccess: (r) => {
      setDraft(r.draft);
      setText(r.draft.summary);
      setPicked(r.draft.actionItems.map(() => true));
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 7000 }),
  });
  const add = useMutation({
    mutationFn: () =>
      onAddItems(
        draft!.actionItems
          .filter((_, i) => picked[i])
          .map((a) => ({ title: a.title, detail: a.detail || null, ownerSide: a.ownerSide, visibleToFranchisee: a.visibleToFranchisee })),
      ),
    onSuccess: () => {
      showToast('Action items added.');
      setDraft((d) => (d ? { ...d, actionItems: [] } : d));
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  return (
    <section style={card}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, color: NAVY }}>AI recap from Teams</h3>
      {!draft ? (
        <>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: MUTED }}>
            After the call, draft the summary and action items from the Teams transcript. Nothing is saved or
            shown to the franchisee until you use it.
          </p>
          <button onClick={() => make.mutate()} disabled={make.isPending} style={btn}>
            {make.isPending ? 'Reading the transcript…' : 'Draft from the Teams transcript'}
          </button>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 10, fontSize: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
            Draft: check it against what was said, and remove anything about a client or caregiver.
          </p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} style={{ width: '100%', boxSizing: 'border-box', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }} />
          <div>
            <button onClick={() => { onUseSummary(text); showToast('Summary copied in. It saves automatically.'); }} style={btn}>
              Use this summary
            </button>
          </div>
          {draft.actionItems.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Suggested action items</div>
              {draft.actionItems.map((a, i) => (
                <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                  <input type="checkbox" checked={!!picked[i]} onChange={(e) => setPicked((p) => p.map((v, j) => (j === i ? e.target.checked : v)))} />
                  <span>
                    <strong>{a.title}</strong>{' '}
                    <span style={{ color: MUTED, fontSize: 12 }}>
                      ({a.ownerSide === 'amada' ? 'Amada' : 'Franchise partner'}{a.visibleToFranchisee ? ', visible to them' : ', internal'})
                    </span>
                    {a.detail && <div style={{ color: MUTED, fontSize: 13 }}>{a.detail}</div>}
                  </span>
                </label>
              ))}
              <button onClick={() => add.mutate()} disabled={add.isPending || !picked.some(Boolean)} style={btn}>
                {add.isPending ? 'Adding…' : `Add ${picked.filter(Boolean).length} action item${picked.filter(Boolean).length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
          <button onClick={() => setDraft(null)} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 13, justifySelf: 'start', padding: 0 }}>
            Discard draft
          </button>
        </div>
      )}
    </section>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '22px 24px 70px', maxWidth: 1280, margin: '0 auto' }}>
      <p style={{ margin: 0 }}>
        <Link to="/dmr" style={{ color: '#2563eb' }}>← DMR calls</Link>
      </p>
      {children}
    </div>
  );
}

const card: React.CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff' };
const label: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: MUTED, fontWeight: 700, marginBottom: 2 };
const textarea: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit',
};
const primaryBtn: React.CSSProperties = {
  background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, padding: '0 8px 0 0', fontWeight: 600,
};
const btn: React.CSSProperties = {
  background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
